import { ownerThanks } from '@/services/claude-fleet/claude-fleet.runners'

import { MyWorkApi } from './my-work.api'
import { actionRequestBody, myWorkErrorText } from './my-work.utils'
import type { MySweep, StartSweepResult } from './my-work-sweep.types'
import type {
  AutoMergeRequest,
  AutoMergeResponse,
  ActionChoices,
  ActionOffer,
  AnswerDecisionRequest,
  AnswerDecisionResult,
  MachineThanks,
  MergePreviewResponse,
  MergePrsResponse,
  MergeSettings,
  MyActionsResponse,
  MyWork,
  MyWorkCounts,
  PrDetailsResponse,
  PrRefreshResponse,
  SaveMergeSettingsRequest,
  MyWorkQuery,
  StartActionResult
} from './my-work.types'

/** The fleet refuses a merge request naming more PRs than this (its MERGE_ALL_MAX). */
export const MERGE_BATCH_MAX = 20

/**
 * My work service — the fleet's /fleet/me API for the signed-in engineer's own work.
 * No reactive state here; the cached layer and the store own loading and error.
 */
export function useMyWorkService() {
  /** A section the fleet could not read arrives missing; the page shows it as empty, with the fleet's note. */
  const getMyWork = async (query: MyWorkQuery = {}): Promise<MyWork> => {
    const work = await MyWorkApi.getMyWork(Boolean(query.refresh))
    return {
      ...work,
      notes: work.notes ?? [],
      tickets: work.tickets ?? [],
      myPrs: work.myPrs ?? [],
      reviewRequested: work.reviewRequested ?? [],
      decisions: work.decisions ?? [],
      recent: work.recent ?? []
    }
  }

  const listActions = async (): Promise<MyActionsResponse> => {
    const response = await MyWorkApi.listActions()
    return { requests: response.requests ?? [], cap: response.cap }
  }

  const startAction = async (
    offer: ActionOffer,
    target: string,
    choices: ActionChoices = {}
  ): Promise<StartActionResult> => MyWorkApi.startAction(actionRequestBody(offer, target, choices))

  const answerDecision = async (answer: AnswerDecisionRequest): Promise<AnswerDecisionResult> =>
    MyWorkApi.answerDecision(answer)

  /** A live progress check of the latest sweep; null when the person has none. */
  const getSweep = async (): Promise<MySweep | null> => (await MyWorkApi.getSweep()).sweep ?? null

  const startSweep = async (): Promise<StartSweepResult> => MyWorkApi.startSweep()

  /** This engineer's laptop runs, matched by email; null when the fleet sends none for them. */
  const getMyMachineThanks = async (email: string): Promise<MachineThanks | null> =>
    ownerThanks(await MyWorkApi.listRunnerOwners(), email)

  /**
   * Asks the person's own machine to merge each PR; each answer says whether it was queued. Sent in
   * batches, since the fleet takes at most MERGE_BATCH_MAX PRs per request. A batch that fails after
   * earlier ones were queued keeps their answers, so the page still shows the PRs that will merge.
   */
  const mergePrs = async (targets: string[]): Promise<MergePrsResponse> => {
    const results: MergePrsResponse['results'] = []
    for (let start = 0; start < targets.length; start += MERGE_BATCH_MAX) {
      const batch = targets.slice(start, start + MERGE_BATCH_MAX)
      try {
        const response = await MyWorkApi.mergePrs({ targets: batch })
        results.push(...(response.results ?? []))
      } catch (e) {
        if (start === 0) throw e
        const message = myWorkErrorText(e, 'The merge could not be queued.')
        results.push(...targets.slice(start).map(target => ({ target, ok: false, message })))
        break
      }
    }
    return { results }
  }

  const setAutoMerge = async (request: AutoMergeRequest): Promise<AutoMergeResponse> => MyWorkApi.setAutoMerge(request)

  const refreshPr = async (repo: string, id: number): Promise<PrRefreshResponse> =>
    MyWorkApi.refreshPr(repo, id)

  const getPrDetails = async (targets: string[]): Promise<PrDetailsResponse> => MyWorkApi.getPrDetails(targets)

  const saveMergeSettings = async (settings: SaveMergeSettingsRequest): Promise<MergeSettings> =>
    MyWorkApi.saveMergeSettings(settings)

  const previewMergeSettings = async (
    settings: SaveMergeSettingsRequest
  ): Promise<MergePreviewResponse> => MyWorkApi.previewMergeSettings(settings)

  /** The side nav badge's counts, from the fleet's cached page. An older fleet answers 404. */
  const getCounts = async (): Promise<MyWorkCounts> => MyWorkApi.getCounts()

  return {
    getMyWork,
    listActions,
    startAction,
    answerDecision,
    getSweep,
    startSweep,
    getMyMachineThanks,
    mergePrs,
    setAutoMerge,
    saveMergeSettings,
    previewMergeSettings,
    refreshPr,
    getPrDetails,
    getCounts
  }
}
